import { useCallback } from 'react';
import { useRecoilCallback } from 'recoil';
import Supercluster from 'supercluster';
import { Stop } from '@app/shared/models/tour.type';
import { atom_mobileListHeight, atom_ctaPanelHeight } from '@app/shared/state/list-mode.atom';

interface UseMapNavigationProps {
  mapRef: React.MutableRefObject<any>;
  supercluster: Supercluster | null;
  stops: Stop[] | undefined;
  activeStopId?: string;
  isDesktop?: boolean;
}

export function useMapNavigation({
  mapRef,
  supercluster,
  stops,
  activeStopId,
  isDesktop = false,
}: UseMapNavigationProps) {
  // On-demand reads so the hook doesn't re-render on panel-height ticks.
  const getBottomObstruction = useRecoilCallback(({ snapshot }) => () => {
    const listH = snapshot.getLoadable(atom_mobileListHeight).contents as number;
    const ctaH = snapshot.getLoadable(atom_ctaPanelHeight).contents as number;
    const result = Math.max(listH || 0, ctaH || 0);
    console.log('[FIT-DEBUG] getBottomObstruction READ: listH=', listH, 'ctaH=', ctaH, '→', result, 'at', Date.now());
    return result;
  }, []);
  /**
   * Flies to a selected stop, calculating the optimal zoom level to avoid clusters
   */
  const flyToSelectedStop = useCallback(
    (stopId?: string) => {
      const selectedStop = stops?.find(
        (stop: Stop) => stop.id === (stopId || activeStopId)
      );
      if (!selectedStop || !mapRef.current || !supercluster) {
        return;
      }

      let finalZoom = 15;

      const bounds = mapRef.current.getBounds();
      const lat = selectedStop.stop_location.latitude;
      const lng = selectedStop.stop_location.longitude;
      const bbox: [number, number, number, number] = [
        Math.min(bounds.getWest(), lng),
        Math.min(bounds.getSouth(), lat),
        Math.max(bounds.getEast(), lng),
        Math.max(bounds.getNorth(), lat),
      ];

      // Find the zoom level where the stop becomes an individual marker (not clustered)
      // Keep increasing zoom until the stop is no longer in a cluster, up to max zoom 18
      for (let zoom = 15; zoom <= 18; zoom++) {
        const clustersAtZoom = supercluster.getClusters(bbox, zoom);
        const feature = clustersAtZoom.find((c: any) => {
          if (c.properties.cluster) {
            // Check if our stop is inside this cluster
            return supercluster
              .getLeaves(c.id, Infinity)
              .some((leaf: any) => leaf.properties.id === selectedStop.id);
          }
          // Check if this is our individual stop
          return c.properties.id === selectedStop.id;
        });

        if (feature && !feature.properties.cluster) {
          // Found the stop as an individual marker (not clustered)
          finalZoom = zoom;
          break;
        } else if (feature && feature.properties.cluster) {
          // Still in a cluster at this zoom level, continue to next zoom
          finalZoom = Math.min(zoom + 1, 18);
        }
      }

      const currentZoom = mapRef.current.getZoom();
      if (currentZoom > 15 && finalZoom < currentZoom) {
        finalZoom = currentZoom;
      }

      const mapCenter = mapRef.current.getCenter();
      const dLat = mapCenter.lat - lat;
      const dLng = mapCenter.lng - lng;
      const distance = Math.sqrt(dLat * dLat + dLng * dLng);

      // Use two duration profiles depending on whether the stop is inside current viewport bounds
      const isInsideBounds = mapRef.current.getBounds().contains([lat, lng]);
      const insideDuration = Math.min(Math.max(distance * 80, 0.5), 1.2); // quicker for in-bounds
      const outsideDuration = Math.min(Math.max(distance * 120, 0.8), 3.2); // longer for out-of-bounds
      const duration = isInsideBounds ? insideDuration : outsideDuration;

      // Calculate latitude offset for mobile bottom obstruction (list panel OR CTA panel)
      // Only apply on mobile (not desktop) when something is showing at the bottom
      let targetLat = lat;
      let targetLng = lng;
      const bottomObstruction = getBottomObstruction();
      if (!isDesktop && bottomObstruction > 0) {
        const topBarHeight = 105; // Mobile header bar height
        const offsetPixels = (bottomObstruction - topBarHeight) / 2;
        const point = mapRef.current.project([lat, lng], finalZoom);
        point.y += offsetPixels; // shift south in pixel space
        const offsetLatLng = mapRef.current.unproject(point, finalZoom);
        targetLat = offsetLatLng.lat;
        targetLng = offsetLatLng.lng;
      }

      mapRef.current.flyTo([targetLat, targetLng], finalZoom, {
        animate: true,
        duration: duration,
        easeLinearity: 0.35,
      });
    },
    [mapRef, supercluster, stops, activeStopId, isDesktop, getBottomObstruction]
  );

  /**
   * Handles cluster click by expanding it one level (may result in smaller clusters + individual markers)
   */
  const handleClusterClick = useCallback(
    (cluster: any) => {
      if (!supercluster || !mapRef.current) {
        return;
      }

      // Get the expansion zoom - the zoom level at which this cluster breaks apart
      const expansionZoom = supercluster.getClusterExpansionZoom(cluster.id);

      // Cap at max zoom 18
      const targetZoom = Math.min(expansionZoom, 18);

      mapRef.current.flyTo(
        [cluster.geometry.coordinates[1], cluster.geometry.coordinates[0]],
        targetZoom,
        {
          animate: true,
          duration: 0.8,
          easeLinearity: 0.25,
        }
      );
    },
    [supercluster, mapRef]
  );

  const flyToFitStops = useCallback(
    (stopsToFit: Stop[], options?: { padding?: number; maxZoom?: number }) => {
      if (!stopsToFit || stopsToFit.length === 0 || !mapRef.current) {
        return;
      }
      if (stopsToFit.length === 1) {
        flyToSelectedStop(stopsToFit[0].id);
        return;
      }

      // Calculate bounds that contain all provided stops
      let minLat = Infinity;
      let maxLat = -Infinity;
      let minLng = Infinity;
      let maxLng = -Infinity;

      stopsToFit.forEach((stop) => {
        const lat = stop.stop_location.latitude;
        const lng = stop.stop_location.longitude;
        minLat = Math.min(minLat, lat);
        maxLat = Math.max(maxLat, lat);
        minLng = Math.min(minLng, lng);
        maxLng = Math.max(maxLng, lng);
      });

      const bounds: [[number, number], [number, number]] = [
        [minLat, minLng],
        [maxLat, maxLng],
      ];

      const padding = options?.padding ?? 50;
      const maxZoom = options?.maxZoom ?? 15;
      const topBarHeight = 105;
      const topPadding = !isDesktop ? topBarHeight : padding;
      const bottomObstruction = getBottomObstruction();
      const bottomPadding = !isDesktop && bottomObstruction > 0
        ? bottomObstruction
        : padding;

      const viewportH = typeof window !== 'undefined' ? window.innerHeight : 0;
      const viewportW = typeof window !== 'undefined' ? window.innerWidth : 0;
      const visibleH = viewportH - topPadding - bottomPadding;
      const latSpan = maxLat - minLat;
      const lngSpan = maxLng - minLng;
      // Find the outermost stops in each direction — useful to spot outliers
      // that might be dragging the bbox bigger than what's visually there.
      const northmost = stopsToFit.reduce((a, s) => s.stop_location.latitude > a.stop_location.latitude ? s : a, stopsToFit[0]);
      const southmost = stopsToFit.reduce((a, s) => s.stop_location.latitude < a.stop_location.latitude ? s : a, stopsToFit[0]);
      const eastmost = stopsToFit.reduce((a, s) => s.stop_location.longitude > a.stop_location.longitude ? s : a, stopsToFit[0]);
      const westmost = stopsToFit.reduce((a, s) => s.stop_location.longitude < a.stop_location.longitude ? s : a, stopsToFit[0]);
      console.log('[FIT-DEBUG] BBOX EXTREMES:', {
        north: { id: northmost.id, title: (northmost as any).s_title, lat: northmost.stop_location.latitude },
        south: { id: southmost.id, title: (southmost as any).s_title, lat: southmost.stop_location.latitude },
        east: { id: eastmost.id, title: (eastmost as any).s_title, lng: eastmost.stop_location.longitude },
        west: { id: westmost.id, title: (westmost as any).s_title, lng: westmost.stop_location.longitude },
      });
      // Predict the zoom Leaflet WILL pick before flying. Compares with what we get.
      const predictedZoom = mapRef.current.getBoundsZoom(bounds, false, [
        Math.max(topPadding, bottomPadding),
        0,
      ]);
      console.log('[FIT-DEBUG] flyToFitStops FIRE:', {
        viewport: `${viewportW}x${viewportH}`,
        topPadding,
        bottomPadding,
        visibleHeight: visibleH,
        visibleArea: `${viewportW}x${visibleH}`,
        stopCount: stopsToFit.length,
        bboxLat: latSpan.toFixed(6),
        bboxLng: lngSpan.toFixed(6),
        bounds,
        maxZoom,
        predictedZoom,
        currentZoom: mapRef.current.getZoom(),
        at: Date.now(),
      });

      mapRef.current.flyToBounds(bounds, {
        animate: true,
        duration: 1.2,
        easeLinearity: 0.35,
        paddingTopLeft: [0, topPadding] as [number, number],
        paddingBottomRight: [0, bottomPadding] as [number, number],
        maxZoom: maxZoom,
      });

      // Log actual final zoom + center after animation completes
      const onMoveEnd = () => {
        console.log('[FIT-DEBUG] flyToFitStops SETTLED:', {
          finalZoom: mapRef.current.getZoom(),
          finalCenter: mapRef.current.getCenter(),
          actualBoundsAtZoom: mapRef.current.getBounds(),
        });
        mapRef.current.off('moveend', onMoveEnd);
      };
      mapRef.current.on('moveend', onMoveEnd);

    },
    [mapRef, flyToSelectedStop, isDesktop, getBottomObstruction]
  );

  const flyToBoundsWithCoordinates = useCallback(
    (coordinates: Array<{ lat: number; lng: number }>, options?: { padding?: number; maxZoom?: number }) => {
      if (!coordinates || coordinates.length === 0 || !mapRef.current) {
        return;
      }
      // If only one coordinate, fly to it directly
      if (coordinates.length === 1) {
        const { lat, lng } = coordinates[0];
        mapRef.current.flyTo([lat, lng], options?.maxZoom ?? 15, {
          animate: true,
          duration: 1.0,
          easeLinearity: 0.35,
        });
        return;
      }

      // Calculate bounds that contain all coordinates
      let minLat = Infinity;
      let maxLat = -Infinity;
      let minLng = Infinity;
      let maxLng = -Infinity;

      coordinates.forEach(({ lat, lng }) => {
        minLat = Math.min(minLat, lat);
        maxLat = Math.max(maxLat, lat);
        minLng = Math.min(minLng, lng);
        maxLng = Math.max(maxLng, lng);
      });

      const bounds: [[number, number], [number, number]] = [
        [minLat, minLng],
        [maxLat, maxLng],
      ];

      const padding = options?.padding ?? 50;
      const maxZoom = options?.maxZoom ?? 15;
      mapRef.current.flyToBounds(bounds, {
        animate: true,
        duration: 1.2,
        easeLinearity: 0.35,
        padding: [padding, padding],
        maxZoom: maxZoom,
      });
    },
    [mapRef]
  );

  return {
    flyToSelectedStop,
    flyToFitStops,
    flyToBoundsWithCoordinates,
    handleClusterClick,
  };
}
